import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Plus, CalendarBlank, Warning, PencilSimple, CaretLeft, CaretRight, Flag,
} from '@phosphor-icons/react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, isWithinInterval, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

function getDayBg(isSelected, isToday, hasDelayed) {
  if (isSelected) return 'bg-[#FF6B00] text-white ring-2 ring-[#FF6B00] ring-offset-1';
  if (isToday) return 'bg-orange-100 text-[#FF6B00] ring-1 ring-[#FF6B00]/30';
  if (hasDelayed) return 'bg-red-50 ring-1 ring-red-200';
  return 'hover:bg-gray-100';
}

function getDotColor(isSelected, status) {
  if (isSelected) return 'bg-white';
  if (status === 'delayed') return 'bg-red-500';
  if (status === 'completed') return 'bg-emerald-500';
  if (status === 'in_progress') return 'bg-blue-500';
  return 'bg-amber-400';
}

function getTaskCardStyle(task) {
  if (task.task_type === 'milestone') return 'bg-orange-50 border-l-[#FF6B00]';
  if (task.status === 'delayed') return 'bg-red-50 border-l-red-500';
  if (task.status === 'completed') return 'bg-gray-50 border-l-emerald-500';
  if (task.status === 'in_progress') return 'bg-gray-50 border-l-blue-500';
  return 'bg-gray-50 border-l-amber-400';
}

export function CalendarView({
  filteredTasks, chantiers, currentMonth, setCurrentMonth,
  selectedDate, setSelectedDate, getChantierName,
  onCreateSheet, onEditTask,
}) {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getTasksForDay = (date) => filteredTasks.filter(task => {
    if (!task.start_date || !task.end_date) return false;
    try { return isWithinInterval(date, { start: parseISO(task.start_date), end: parseISO(task.end_date) }); } catch (err) { return false; }
  });

  const selectedDateTasks = getTasksForDay(selectedDate);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-2">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
              <CaretLeft size={24} />
            </Button>
            <CardTitle className="text-lg">
              {format(currentMonth, 'MMMM yyyy', { locale: fr })}
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
              <CaretRight size={24} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-[#4B5563] py-2">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: (monthStart.getDay() + 6) % 7 }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}
            {calendarDays.map(day => {
              const dayTasks = getTasksForDay(day);
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              const hasDelayed = dayTasks.some(t => t.status === 'delayed');
              const hasMilestone = dayTasks.some(t => t.task_type === 'milestone');
              return (
                <button key={day.toISOString()} onClick={() => setSelectedDate(day)}
                  className={`aspect-square p-1 rounded-lg transition-all relative ${getDayBg(isSelected, isToday, hasDelayed)}`}>
                  <span className={`text-sm font-medium ${isSelected ? 'text-white' : ''}`}>
                    {format(day, 'd')}
                  </span>
                  {hasMilestone && !isSelected && (
                    <Flag size={10} weight="fill" className="absolute top-0.5 right-0.5 text-[#FF6B00]" />
                  )}
                  {dayTasks.length > 0 && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {dayTasks.slice(0, 3).map((task) => (
                        <div key={task.id} className={`w-1.5 h-1.5 rounded-full ${getDotColor(isSelected, task.status)}`} />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {format(selectedDate, 'EEEE d MMMM', { locale: fr })}
            </CardTitle>
            <Button size="sm" onClick={() => onCreateSheet(selectedDate)} className="bg-[#FF6B00]">
              <Plus size={16} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {selectedDateTasks.length > 0 ? selectedDateTasks.map(task => (
            <div key={task.id} className={`p-3 rounded-lg border-l-4 ${getTaskCardStyle(task)}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {task.task_type === 'milestone' && <Flag size={14} weight="fill" className="text-[#FF6B00] flex-shrink-0" />}
                    {task.status === 'delayed' && <Warning size={14} weight="fill" className="text-red-500 animate-pulse flex-shrink-0" />}
                    <span className="font-medium text-sm truncate block">{task.title}</span>
                  </div>
                  <p className="text-xs text-[#4B5563]">{getChantierName(task.chantier_id)}</p>
                  {task.lot && <p className="text-[10px] text-[#9CA3AF]">Lot: {task.lot}</p>}
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEditTask(task)}>
                  <PencilSimple size={16} />
                </Button>
              </div>
            </div>
          )) : (
            <div className="text-center py-8 text-[#4B5563]">
              <CalendarBlank size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucune tâche ce jour</p>
              <Button variant="link" className="text-[#FF6B00] mt-2" onClick={() => onCreateSheet(selectedDate)}>
                Ajouter une tâche
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
